import React, { useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { Link } from "react-router-dom";
import BreadCrumb from "Components/Common/BreadCrumb";
import TableContainer from "Components/Common/TableContainer";
import Loader from "Components/Common/Loader";
import { Tooltip } from "react-tooltip";
import { formatDateTime } from "helpers/date_utils";
import { IsValidPermissions } from "../../../Routes/AuthProtected";
import * as Yup from "yup";
import { useFormik } from "formik";

import {
  useGetStockManagementQuery,
  useCreateStockProjectAllocationMutation,
  useCreateStockManagementMutation,
  useUpdateStockManagementMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { useGetProjectsByDropDownQuery } from "slices/projects/projectSlice";

const StockManagementListView = () => {
  const [page, setPageState] = useState({
    page: 1,
    pageSize: 10,
    search: "",
  });

  // Modal states
  const [allocationModal, setAllocationModal] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [selectedStock, setSelectedStock] = useState<any>(null);
  const [stockToEdit, setStockToEdit] = useState<any>(null);
  const [stockType, setStockType] = useState("manual"); // Default to manual
  const [createStockFiles, setCreateStockFiles] = useState<File[]>([]);
  const [transferToggleLoading, setTransferToggleLoading] = useState<
    Record<number, boolean>
  >({});

  // Queries
  const {
    data: stockData,
    isLoading,
    isFetching,
    refetch,
  } = useGetStockManagementQuery(page);

  const { data: projectData } = useGetProjectsByDropDownQuery(undefined);

  // Mutations
  const [createAllocation, { isLoading: isAllocating }] =
    useCreateStockProjectAllocationMutation();

  const [createStock, { isLoading: isCreatingStock }] =
    useCreateStockManagementMutation();

  const [updateStock, { isLoading: isUpdatingStock }] =
    useUpdateStockManagementMutation();

  const extractErrorMessage = (error: any) => {
    return (
      error?.data?.detail ||
      error?.data?.error ||
      error?.error ||
      "Failed to update transfer flag. Please try again."
    );
  };

  const handleTransferFlagToggle = async (stock: any, checked: boolean) => {
    try {
      setTransferToggleLoading((prev) => ({ ...prev, [stock.id]: true }));
      await updateStock({
        id: stock.id,
        productData: { is_transfer_to_another_project: checked },
      }).unwrap();
      refetch();
    } catch (error) {
      console.error("Transfer flag update failed:", error);
      alert(String(extractErrorMessage(error)));
    } finally {
      setTransferToggleLoading((prev) => ({ ...prev, [stock.id]: false }));
    }
  };

  // Status badge helper with updated statuses
  const getStatusBadge = (status: string, display?: string) => {
    const colorMap: Record<string, string> = {
      available: "success",
      partially_allocated: "warning",
      fully_allocated: "info",
      consumed: "secondary",
      reserved: "dark",
    };

    return (
      <Badge color={colorMap[status] || "secondary"}>{display || status}</Badge>
    );
  };

  // Stock type display helper
  const getStockTypeDisplay = (type: string) => {
    const typeMap: Record<string, string> = {
      balance_from_pr: "Balance from PR",
      manual: "Manual",
      transfer: "Transfer",
      adjustment: "Adjustment",
    };

    return typeMap[type] || type;
  };

  const getLatestAllocationReference = (stock: any) => {
    const allocations = Array.isArray(stock?.allocations) ? stock.allocations : [];
    if (!allocations.length) return null;
    const latest = allocations[0];
    const projectName = latest?.target_project_name || latest?.project_name || "Unknown Project";
    const poNumber = latest?.purchase_order_number;
    if (poNumber) {
      return `${poNumber} -> ${projectName}`;
    }
    return `Allocation -> ${projectName}`;
  };

  const getAllocationTooltipContent = (stock: any) => {
    const allocations = Array.isArray(stock?.allocations) ? stock.allocations : [];
    if (!allocations.length) return "No allocation references";
    return allocations
      .slice(0, 6)
      .map((allocation: any) => {
        const projectName =
          allocation?.target_project_name || allocation?.project_name || "Unknown Project";
        const poNumber = allocation?.purchase_order_number || "No PO";
        const qty = allocation?.quantity || "0";
        return `${poNumber} -> ${projectName} | Qty ${qty}`;
      })
      .join("\n");
  };

  // Calculate missing fields
  const calculateMissingFields = (values: any) => {
    const { unit_price, actual_quantity, total_cost } = values;

    // Convert to numbers
    const unitPriceNum = parseFloat(unit_price) || 0;
    const quantityNum = parseFloat(actual_quantity) || 0;
    const totalCostNum = parseFloat(total_cost) || 0;

    let calculatedUnitPrice = unitPriceNum;
    let calculatedTotalCost = totalCostNum;

    // Case 1: total_cost missing, unit_price and actual_quantity provided
    if (!total_cost && unit_price && actual_quantity && quantityNum > 0) {
      calculatedTotalCost = unitPriceNum * quantityNum;
    }

    // Case 2: unit_price missing, total_cost and actual_quantity provided
    if (!unit_price && total_cost && actual_quantity && quantityNum > 0) {
      calculatedUnitPrice = totalCostNum / quantityNum;
    }

    return {
      unit_price: calculatedUnitPrice.toFixed(2),
      total_cost: calculatedTotalCost.toFixed(2),
    };
  };

  // Validation schema for allocation
  const allocationSchema = Yup.object().shape({
    project: Yup.number()
      .required("Project is required")
      .test(
        "not-same-project",
        "Transfer project cannot be the same as source project",
        function (value) {
          const { source_project } = this.parent;
          return !source_project || value !== source_project;
        },
      ),
    quantity: Yup.number()
      .required("Quantity is required")
      .min(1, "Quantity must be at least 1")
      .max(
        Yup.ref("balance_quantity"),
        "Quantity cannot exceed available balance",
      ),
    allocation_type: Yup.string().required("Allocation type is required"),
    allocated_date: Yup.date().required("Allocated date is required"),
    transferred_date: Yup.date().required("Transferred date is required"),
    notes: Yup.string().nullable(),
  });

  // Validation schema for creating manual stock (aligned with API: source_project optional for manual; balance_quantity defaults to actual_quantity on create)
  const createStockSchema = Yup.object().shape({
    stock_type: Yup.string().required("Stock type is required"),
    source_project: Yup.number()
      .nullable()
      .when("stock_type", {
        is: "balance_from_pr",
        then: (schema) => schema.required("Source project is required for PR balance"),
        otherwise: (schema) => schema.nullable(),
      }),
    actual_quantity: Yup.number()
      .required("Quantity is required")
      .min(0.01, "Quantity must be greater than 0"),
    balance_quantity: Yup.number()
      .min(0, "Balance quantity cannot be negative")
      .test(
        "not-greater-than-actual",
        "Balance quantity cannot exceed actual quantity",
        function (value) {
          const { actual_quantity } = this.parent;
          if (value == null || (typeof value === "string" && value === ""))
            return true;
          const numActual = Number(actual_quantity);
          return Number(value) <= numActual;
        },
      )
      .nullable(),
    currency: Yup.string().required("Currency is required"),
    status: Yup.string().required("Status is required"),

    // For manual/transfer/adjustment: require product name or item code (API resolves product from these)
    manual_product_name: Yup.string().when("stock_type", {
      is: (type: string) => ["manual", "adjustment", "transfer"].includes(type),
      then: (schema) =>
        schema.test(
          "manual-product-or-code",
          "Product name or model number is required",
          function (value) {
            const { model_number } = this.parent;
            return !!(value && value.trim()) || !!(model_number && model_number.trim());
          },
        ),
      otherwise: (schema) => schema.nullable(),
    }),

    // For balance_from_pr, require purchase request
    source_purchase_request: Yup.number().when("stock_type", {
      is: "balance_from_pr",
      then: (schema) => schema.required("Purchase request is required"),
      otherwise: (schema) => schema.nullable(),
    }),

    model_number: Yup.string().nullable(),

    // Either unit_price or total_cost for manual/adjustment/transfer (API can derive the other)
    unit_price: Yup.number()
      .nullable()
      .test(
        "price-or-total",
        "Either unit price or total cost must be provided",
        function (value) {
          const { total_cost, stock_type } = this.parent;
          if (["manual", "adjustment", "transfer"].includes(stock_type)) {
            return !!value || !!total_cost;
          }
          return true;
        },
      ),
    total_cost: Yup.number().nullable(),

    notes: Yup.string().nullable(),
  });

  // Formik for allocation
  const allocationFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      project: "",
      quantity: "",
      allocation_type: "allocated",
      allocated_date: new Date().toISOString().split("T")[0],
      transferred_date: new Date().toISOString().split("T")[0],
      notes: "",
      stock_management: selectedStock?.id || "",
      source_project: selectedStock?.source_project || "",
      balance_quantity: selectedStock?.balance_quantity || 0,
    },
    validationSchema: allocationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        // Get auth user from localStorage
        const authUserString = localStorage.getItem("authUser");
        let performed_by = null;

        if (authUserString) {
          const authUser = JSON.parse(authUserString);
          performed_by = authUser?.employee_id || authUser?.id;
        }

        const payload = {
          stock_management: parseInt(values.stock_management),
          project: parseInt(values.project),
          quantity: values.quantity.toString(),
          allocation_type: values.allocation_type,
          allocated_date: values.allocated_date,
          transferred_date: values.transferred_date,
          performed_by: performed_by,
          notes: values.notes,
        };

        await createAllocation(payload).unwrap();

        // Close modal and reset form
        setAllocationModal(false);
        resetForm();
        setSelectedStock(null);

        // Refresh stock data
        refetch();

        // Show success message
        alert("Allocation created successfully!");
      } catch (error) {
        console.error("Allocation failed:", error);
        alert("Failed to create allocation. Please try again.");
      }
    },
  });

  // Formik for creating stock
  const createStockFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      stock_type: "manual",
      source_project: "",
      source_purchase_request: "",
      actual_quantity: "",
      balance_quantity: "",
      currency: "SGD",
      status: "available",
      manual_product_name: "",
      model_number: "",
      unit_price: "",
      total_cost: "",
      notes: "",
    },
    validationSchema: createStockSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        // Calculate missing fields for manual/adjustment/transfer (API also does this; we send for UX)
        let calculatedFields = { unit_price: "", total_cost: "" };

        if (["manual", "adjustment", "transfer"].includes(values.stock_type)) {
          calculatedFields = calculateMissingFields(values);
        }

        const actualQty = values.actual_quantity.toString();
        const balanceQty =
          values.balance_quantity !== "" && values.balance_quantity != null
            ? values.balance_quantity.toString()
            : actualQty;

        const payload: any = {
          stock_type: values.stock_type,
          actual_quantity: actualQty,
          balance_quantity: balanceQty,
          currency: values.currency,
          status: values.status,
          notes: values.notes || null,
        };

        if (values.source_project !== "" && values.source_project != null) {
          payload.source_project = parseInt(values.source_project, 10);
        } else if (values.stock_type !== "balance_from_pr") {
          payload.source_project = null;
        }

        switch (values.stock_type) {
          case "balance_from_pr":
            payload.source_purchase_request = parseInt(
              values.source_purchase_request,
              10,
            );
            if (payload.source_project == null && values.source_project) {
              payload.source_project = parseInt(values.source_project, 10);
            }
            break;

          case "manual":
          case "adjustment":
          case "transfer":
            payload.manual_product_name =
              values.manual_product_name?.trim() || "";
            if (values.model_number?.trim()) {
              payload.model_number = values.model_number.trim();
            }
            if (calculatedFields.unit_price) {
              payload.unit_price = calculatedFields.unit_price;
            }
            if (calculatedFields.total_cost) {
              payload.total_cost = calculatedFields.total_cost;
            }
            break;
        }

        // Send as multipart when file attachments are selected; otherwise plain JSON.
        if (createStockFiles.length > 0) {
          const formData = new FormData();
          Object.entries(payload).forEach(([key, val]) => {
            if (val !== undefined && val !== null) {
              formData.append(key, String(val));
            }
          });
          createStockFiles.forEach((file) =>
            formData.append("attachments", file)
          );
          await createStock(formData).unwrap();
        } else {
          await createStock(payload).unwrap();
        }

        // Close modal and reset form
        setCreateModal(false);
        setCreateStockFiles([]);
        resetForm();

        // Refresh stock data
        refetch();

        // Show success message
        alert("Stock created successfully!");
      } catch (error) {
        console.error("Stock creation failed:", error);
        alert("Failed to create stock. Please try again.");
      }
    },
  });

  // Validation schema for edit (same as create for manual/transfer/adjustment fields)
  const editStockSchema = Yup.object().shape({
    actual_quantity: Yup.number()
      .required("Quantity is required")
      .min(0.01, "Quantity must be greater than 0"),
    balance_quantity: Yup.number()
      .min(0, "Balance quantity cannot be negative")
      .test(
        "not-greater-than-actual",
        "Balance quantity cannot exceed actual quantity",
        function (value) {
          const { actual_quantity } = this.parent;
          if (value == null || (typeof value === "string" && value === ""))
            return true;
          return Number(value) <= Number(actual_quantity);
        },
      )
      .nullable(),
    currency: Yup.string().required("Currency is required"),
    status: Yup.string().required("Status is required"),
    manual_product_name: Yup.string()
      .nullable()
      .test(
        "manual-product-or-code",
        "Product name or model number is required",
        function (value) {
          const { model_number } = this.parent;
          return !!(value && String(value).trim()) || !!(model_number && String(model_number).trim());
        },
      ),
    model_number: Yup.string().nullable(),
    unit_price: Yup.number().nullable(),
    total_cost: Yup.number().nullable(),
    notes: Yup.string().nullable(),
  });

  // Formik for editing manual/transfer/adjustment stock
  const editStockFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      stock_type: stockToEdit?.stock_type || "manual",
      source_project: stockToEdit?.source_project ?? "",
      actual_quantity: stockToEdit?.actual_quantity ?? "",
      balance_quantity: stockToEdit?.balance_quantity ?? "",
      currency: stockToEdit?.currency || "SGD",
      status: stockToEdit?.status || "available",
      manual_product_name: stockToEdit?.product_name || "",
      model_number: "",
      unit_price: stockToEdit?.unit_price ?? "",
      total_cost: stockToEdit?.total_cost ?? "",
      notes: stockToEdit?.notes ?? "",
    },
    validationSchema: editStockSchema,
    onSubmit: async (values) => {
      if (!stockToEdit?.id) return;
      try {
        const calculatedFields = calculateMissingFields(values);
        const actualQty = values.actual_quantity.toString();
        const balanceQty =
          values.balance_quantity !== "" && values.balance_quantity != null
            ? values.balance_quantity.toString()
            : actualQty;

        const payload: any = {
          stock_type: values.stock_type,
          actual_quantity: actualQty,
          balance_quantity: balanceQty,
          currency: values.currency,
          status: values.status,
          notes: values.notes || null,
        };

        if (values.source_project !== "" && values.source_project != null) {
          payload.source_project = parseInt(values.source_project, 10);
        } else {
          payload.source_project = null;
        }

        payload.manual_product_name = values.manual_product_name?.trim() || "";
        if (values.model_number?.trim()) payload.model_number = values.model_number.trim();
        if (calculatedFields.unit_price)
          payload.unit_price = calculatedFields.unit_price;
        if (calculatedFields.total_cost)
          payload.total_cost = calculatedFields.total_cost;

        await updateStock({ id: stockToEdit.id, productData: payload }).unwrap();

        setEditModal(false);
        setStockToEdit(null);
        refetch();
        alert("Stock updated successfully!");
      } catch (error) {
        console.error("Stock update failed:", error);
        alert("Failed to update stock. Please try again.");
      }
    },
  });

  // Auto-calculate balance quantity when actual quantity changes (edit form)
  React.useEffect(() => {
    if (editModal && editStockFormik.values.actual_quantity) {
      if (
        !editStockFormik.values.balance_quantity ||
        editStockFormik.values.balance_quantity === ""
      ) {
        editStockFormik.setFieldValue(
          "balance_quantity",
          editStockFormik.values.actual_quantity,
        );
      }
    }
  }, [editModal, editStockFormik.values.actual_quantity]);

  // Auto-calculate total cost or unit price (edit form)
  React.useEffect(() => {
    if (!editModal) return;
    const { unit_price, actual_quantity, total_cost } = editStockFormik.values;
    if (unit_price && actual_quantity && !total_cost) {
      const calculatedTotal = (
        parseFloat(unit_price) * parseFloat(actual_quantity)
      ).toFixed(2);
      editStockFormik.setFieldValue("total_cost", calculatedTotal);
    } else if (
      total_cost &&
      actual_quantity &&
      !unit_price &&
      parseFloat(actual_quantity) > 0
    ) {
      const calculatedUnit = (
        parseFloat(total_cost) / parseFloat(actual_quantity)
      ).toFixed(2);
      editStockFormik.setFieldValue("unit_price", calculatedUnit);
    }
  }, [
    editModal,
    editStockFormik.values.unit_price,
    editStockFormik.values.actual_quantity,
    editStockFormik.values.total_cost,
  ]);

  // Auto-calculate balance quantity when actual quantity changes
  React.useEffect(() => {
    if (createStockFormik.values.actual_quantity) {
      if (
        !createStockFormik.values.balance_quantity ||
        createStockFormik.values.balance_quantity === ""
      ) {
        createStockFormik.setFieldValue(
          "balance_quantity",
          createStockFormik.values.actual_quantity,
        );
      }
    }
  }, [createStockFormik.values.actual_quantity]);

  // Auto-calculate total cost or unit price when relevant fields change
  React.useEffect(() => {
    const { unit_price, actual_quantity, total_cost, stock_type } =
      createStockFormik.values;

    // Only auto-calculate for manual, adjustment, transfer types
    if (["manual", "adjustment", "transfer"].includes(stock_type)) {
      if (unit_price && actual_quantity && !total_cost) {
        const calculatedTotal = (
          parseFloat(unit_price) * parseFloat(actual_quantity)
        ).toFixed(2);
        createStockFormik.setFieldValue("total_cost", calculatedTotal);
      } else if (
        total_cost &&
        actual_quantity &&
        !unit_price &&
        parseFloat(actual_quantity) > 0
      ) {
        const calculatedUnit = (
          parseFloat(total_cost) / parseFloat(actual_quantity)
        ).toFixed(2);
        createStockFormik.setFieldValue("unit_price", calculatedUnit);
      }
    }
  }, [
    createStockFormik.values.unit_price,
    createStockFormik.values.actual_quantity,
    createStockFormik.values.total_cost,
    createStockFormik.values.stock_type,
  ]);

  // Handle stock type change
  const handleStockTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newType = e.target.value;
    setStockType(newType);
    createStockFormik.handleChange(e);

    // Reset certain fields when changing stock type
    if (
      newType !== "manual" &&
      newType !== "adjustment" &&
      newType !== "transfer"
    ) {
      createStockFormik.setFieldValue("unit_price", "");
      createStockFormik.setFieldValue("total_cost", "");
      createStockFormik.setFieldValue("manual_product_name", "");
    }
    if (newType !== "balance_from_pr") {
      createStockFormik.setFieldValue("source_purchase_request", "");
    }
  };

  // Check if stock can be allocated
  const canAllocateStock = (stock: any) => {
    return (
      stock.balance_quantity > 0 &&
      ["available", "partially_allocated", "reserved"].includes(stock.status)
    );
  };

  // Prepare project options for dropdown - EXCLUDE SOURCE PROJECT
  const projectOptions = useMemo(() => {
    if (!projectData) return [];

    const sourceProjectId = selectedStock?.source_project;

    return projectData
      .filter((project: any) => {
        // Exclude the source project from the dropdown
        return project.id !== sourceProjectId;
      })
      .map((project: any) => ({
        value: project.id,
        label: project.name || project.project_name || `Project ${project.id}`,
      }));
  }, [projectData, selectedStock]);

  // Open allocation modal
  const openAllocationModal = (stock: any) => {
    setSelectedStock(stock);

    // Update formik initial values
    allocationFormik.setValues({
      project: "",
      quantity: "",
      allocation_type: "allocated",
      allocated_date: new Date().toISOString().split("T")[0],
      transferred_date: new Date().toISOString().split("T")[0],
      notes: "",
      stock_management: stock.id,
      source_project: stock.source_project,
      balance_quantity: stock.balance_quantity,
    });

    setAllocationModal(true);
  };

  // Open edit modal for manual/transfer/adjustment stock
  const openEditModal = (stock: any) => {
    setStockToEdit(stock);
    editStockFormik.setValues({
      stock_type: stock.stock_type,
      source_project: stock.source_project ?? "",
      actual_quantity: stock.actual_quantity ?? "",
      balance_quantity: stock.balance_quantity ?? "",
      currency: stock.currency || "SGD",
      status: stock.status || "available",
      manual_product_name: stock.product_name || "",
      model_number: "",
      unit_price: stock.unit_price ?? "",
      total_cost: stock.total_cost ?? "",
      notes: stock.notes ?? "",
    });
    setEditModal(true);
  };

  // Get filtered project options (excluding source project) for allocation
  const filteredProjectOptions = useMemo(() => {
    if (!projectData || !selectedStock) return [];

    return projectData
      .filter((project: any) => project.id !== selectedStock.source_project)
      .map((project: any) => ({
        value: project.id,
        label: project.name || project.project_name || `Project ${project.id}`,
      }));
  }, [projectData, selectedStock]);

  const columns = useMemo(
    () => [
      {
        Header: "Product",
        accessor: "product_name",
        Cell: ({ row }: any) => {
          const name = row.original.product_name || "—";
          return (
            <div>
              <div>{name}</div>
              {row.original.model_number && (
                <small className="text-muted">
                  Model No: {row.original.model_number}
                </small>
              )}
            </div>
          );
        },
      },
      {
        Header: "Purchase Request No",
        accessor: "source_purchase_request_number",
        Cell: ({ row }: any) => {
          if (row.original.source_purchase_request) {
            return (
              <Link
                to={`/purchase-request/${row.original.source_purchase_request}`}
                className="text-primary view-stock-class"
              >
                {row.original.source_purchase_request_number}
              </Link>
            );
          }
          return <span className="text-muted">N/A</span>;
        },
      },
      {
        Header: "Supplier",
        accessor: "supplier_name",
      },
      {
        Header: "Actual Qty",
        accessor: "actual_quantity",
        Cell: ({ value }: any) => (
          <span className="fw-semibold">{parseFloat(value).toFixed(2)}</span>
        ),
      },
      {
        Header: "Balance Qty",
        accessor: "balance_quantity",
        Cell: ({ value }: any) => (
          <Badge color="info" pill>
            {parseFloat(value).toFixed(2)}
          </Badge>
        ),
      },
      {
        Header: "Unit Price",
        accessor: "unit_price",
        Cell: ({ value }: any) => (
          <span>{value ? `$${parseFloat(value).toFixed(2)}` : "-"}</span>
        ),
      },
      {
        Header: "Total Cost",
        accessor: "total_cost",
        Cell: ({ value }: any) => (
          <span>{value ? `$${parseFloat(value).toFixed(2)}` : "-"}</span>
        ),
      },
      {
        Header: "Stock Type",
        accessor: "stock_type",
        Cell: ({ value }: any) => (
          <Badge
            color={
              value === "manual"
                ? "primary"
                : value === "balance_from_pr"
                  ? "success"
                  : value === "transfer"
                    ? "warning"
                    : "info"
            }
          >
            {getStockTypeDisplay(value)}
          </Badge>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }: any) =>
          getStatusBadge(row.original.status, row.original.status_display),
      },
      {
        Header: "Source Project",
        accessor: "source_project_name",
      },
      {
        Header: "Project Transfer",
        accessor: "is_transfer_to_another_project",
        Cell: ({ row }: any) => {
          const stock = row.original;
          const isChecked = !!stock.is_transfer_to_another_project;
          const isBusy = !!transferToggleLoading[stock.id];

          return (
            <div className="d-flex align-items-center gap-2">
              <div className="form-check form-switch mb-0">
                <Input
                  type="switch"
                  role="switch"
                  checked={isChecked}
                  disabled={isBusy}
                  onChange={(e) =>
                    handleTransferFlagToggle(stock, e.target.checked)
                  }
                />
              </div>
              <Badge color={isChecked ? "success" : "secondary"}>
                {isChecked ? "Enabled" : "Disabled"}
              </Badge>
            </div>
          );
        },
      },
      {
        Header: "Allocation Ref",
        accessor: "allocations",
        Cell: ({ row }: any) => {
          const stock = row.original;
          const allocations = Array.isArray(stock?.allocations)
            ? stock.allocations
            : [];
          if (!allocations.length) {
            return <span className="text-muted">—</span>;
          }

          const refText = getLatestAllocationReference(stock);
          const tooltipContent = getAllocationTooltipContent(stock);

          return (
            <span
              data-tooltip-id="stock-allocation-ref-tooltip"
              data-tooltip-content={tooltipContent}
              style={{ cursor: "help" }}
            >
              <Badge color="primary" className="text-truncate" style={{ maxWidth: "220px" }}>
                {refText}
              </Badge>
            </span>
          );
        },
      },
      {
        Header: "Created Date",
        accessor: "created_at",
        Cell: ({ value }: any) =>
          value ? formatDateTime(value, "datetime") : "-",
      },
      {
        Header: "Attachments",
        accessor: "attachments",
        Cell: ({ row }: any) => {
          const files = Array.isArray(row.original?.attachments)
            ? row.original.attachments
            : [];
          if (!files.length) {
            return <span className="text-muted">—</span>;
          }
          return (
            <div className="d-flex flex-column gap-1">
              {files.map((att: any) => (
                <a
                  key={att.id}
                  href={att.file_url || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-truncate"
                  style={{ maxWidth: 180 }}
                  title={att.name || `Attachment ${att.id}`}
                >
                  <i className="ri-attachment-2 align-middle me-1"></i>
                  {att.name || `File ${att.id}`}
                </a>
              ))}
            </div>
          );
        },
      },
      {
        Header: "Actions",
        Cell: ({ row }: any) => {
          const stock = row.original;

          return (
            <div>
              <div className="hstack gap-2">
                <Link
                  to={`/stock-management/${stock.id}`}
                  className="btn btn-soft-info btn-sm"
                  title="View"
                >
                  <i className="ri-eye-fill"></i>
                </Link>

                {/* Allocation Button - only for certain statuses */}
                {canAllocateStock(stock) && (
                  <button
                    className="btn btn-soft-success btn-sm"
                    title="Allocate to Project"
                    onClick={() => openAllocationModal(stock)}
                  >
                    <i className="ri-share-forward-line"></i>
                  </button>
                )}

                {/* Edit button for manual/transfer/adjustment - opens modal */}
                {["manual", "transfer", "adjustment"].includes(
                  stock.stock_type,
                ) && (
                  <button
                    type="button"
                    className="btn btn-soft-warning btn-sm"
                    title="Edit"
                    onClick={() => openEditModal(stock)}
                  >
                    <i className="ri-pencil-fill"></i>
                  </button>
                )}
              </div>
            </div>
          );
        },
      },
    ],
    [transferToggleLoading],
  );

  return (
    <React.Fragment>
      {/* Tooltip for actions dropdown */}
      <Tooltip id="actions-tooltip" place="top" />
      <Tooltip id="stock-allocation-ref-tooltip" place="top" />

      {/* Allocation Modal */}
      <Modal
        isOpen={allocationModal}
        toggle={() => {
          setAllocationModal(!allocationModal);
          setSelectedStock(null);
        }}
        centered
        size="lg"
      >
        <ModalHeader
          toggle={() => {
            setAllocationModal(!allocationModal);
            setSelectedStock(null);
          }}
        >
          Allocate Stock to Project
        </ModalHeader>
        <ModalBody>
          {selectedStock && (
            <div className="mb-3">
              <h6>Stock Details</h6>
              <p className="mb-1">
                <strong>Product:</strong>{" "}
                {selectedStock.product_name || "—"}
              </p>
              <p className="mb-1">
                <strong>Stock Type:</strong>{" "}
                <Badge color="info">
                  {getStockTypeDisplay(selectedStock.stock_type)}
                </Badge>
              </p>
              <p className="mb-1">
                <strong>Available Balance:</strong>{" "}
                <Badge color="info">{selectedStock.balance_quantity}</Badge>
              </p>
              <p className="mb-1">
                <strong>Source Project:</strong>{" "}
                {selectedStock.source_project_name
                  ? `${selectedStock.source_project_name} (ID: ${selectedStock.source_project})`
                  : selectedStock.source_project
                    ? `ID: ${selectedStock.source_project}`
                    : "—"}
              </p>
              <p className="mb-1">
                <strong>Purchase Request:</strong>{" "}
                {selectedStock.source_purchase_request_number || "N/A"}
              </p>
            </div>
          )}

          <Form onSubmit={allocationFormik.handleSubmit}>
            {/* Hidden fields */}
            <Input
              type="hidden"
              name="stock_management"
              value={allocationFormik.values.stock_management}
            />
            <Input
              type="hidden"
              name="source_project"
              value={allocationFormik.values.source_project}
            />
            <Input
              type="hidden"
              name="balance_quantity"
              value={allocationFormik.values.balance_quantity}
            />

            {/* Project Selection */}
            <div className="mb-3">
              <Label for="project">Transfer to Project *</Label>
              <Input
                type="select"
                id="project"
                name="project"
                onChange={allocationFormik.handleChange}
                onBlur={allocationFormik.handleBlur}
                value={allocationFormik.values.project}
                invalid={
                  allocationFormik.touched.project &&
                  !!allocationFormik.errors.project
                }
              >
                <option value="">Select Project</option>
                {filteredProjectOptions.map((project: any) => (
                  <option key={project.value} value={project.value}>
                    {project.label}
                  </option>
                ))}
              </Input>
              {allocationFormik.touched.project &&
                allocationFormik.errors.project && (
                  <FormFeedback type="invalid">
                    {allocationFormik.errors.project as string}
                  </FormFeedback>
                )}
              <div className="text-muted mt-1">
                Note: Source project
                {selectedStock?.source_project_name
                  ? ` (${selectedStock.source_project_name}) `
                  : selectedStock?.source_project
                    ? ` (ID: ${selectedStock.source_project}) `
                    : " "}
                is not selectable
              </div>
            </div>

            {/* Quantity */}
            <div className="mb-3">
              <Label for="quantity">Quantity to Allocate *</Label>
              <Input
                type="number"
                id="quantity"
                name="quantity"
                placeholder="Enter quantity"
                min="1"
                max={allocationFormik.values.balance_quantity}
                step="0.01"
                onChange={allocationFormik.handleChange}
                onBlur={allocationFormik.handleBlur}
                value={allocationFormik.values.quantity}
                invalid={
                  allocationFormik.touched.quantity &&
                  !!allocationFormik.errors.quantity
                }
              />
              <div className="text-muted">
                Available: {allocationFormik.values.balance_quantity}
              </div>
              {allocationFormik.touched.quantity &&
                allocationFormik.errors.quantity && (
                  <FormFeedback type="invalid">
                    {allocationFormik.errors.quantity as string}
                  </FormFeedback>
                )}
            </div>

            {/* Allocation Type */}
            <div className="mb-3">
              <Label for="allocation_type">Allocation Type *</Label>
              <Input
                type="select"
                id="allocation_type"
                name="allocation_type"
                onChange={allocationFormik.handleChange}
                onBlur={allocationFormik.handleBlur}
                value={allocationFormik.values.allocation_type}
                invalid={
                  allocationFormik.touched.allocation_type &&
                  !!allocationFormik.errors.allocation_type
                }
              >
                <option value="allocated">Allocated</option>
                <option value="transferred">Transferred</option>
                <option value="consumed">Consumed</option>
              </Input>
              {allocationFormik.touched.allocation_type &&
                allocationFormik.errors.allocation_type && (
                  <FormFeedback type="invalid">
                    {allocationFormik.errors.allocation_type as string}
                  </FormFeedback>
                )}
            </div>

            {/* Allocated Date */}
            <div className="mb-3">
              <Label for="allocated_date">Allocated Date *</Label>
              <Input
                type="date"
                id="allocated_date"
                name="allocated_date"
                onChange={allocationFormik.handleChange}
                onBlur={allocationFormik.handleBlur}
                value={allocationFormik.values.allocated_date}
                invalid={
                  allocationFormik.touched.allocated_date &&
                  !!allocationFormik.errors.allocated_date
                }
              />
              {allocationFormik.touched.allocated_date &&
                allocationFormik.errors.allocated_date && (
                  <FormFeedback type="invalid">
                    {allocationFormik.errors.allocated_date as string}
                  </FormFeedback>
                )}
            </div>

            {/* Transferred Date */}
            <div className="mb-3">
              <Label for="transferred_date">Transferred Date *</Label>
              <Input
                type="date"
                id="transferred_date"
                name="transferred_date"
                onChange={allocationFormik.handleChange}
                onBlur={allocationFormik.handleBlur}
                value={allocationFormik.values.transferred_date}
                invalid={
                  allocationFormik.touched.transferred_date &&
                  !!allocationFormik.errors.transferred_date
                }
              />
              {allocationFormik.touched.transferred_date &&
                allocationFormik.errors.transferred_date && (
                  <FormFeedback type="invalid">
                    {allocationFormik.errors.transferred_date as string}
                  </FormFeedback>
                )}
            </div>

            {/* Notes */}
            <div className="mb-3">
              <Label for="notes">Notes</Label>
              <Input
                type="textarea"
                id="notes"
                name="notes"
                rows="3"
                placeholder="Add any notes about this allocation"
                onChange={allocationFormik.handleChange}
                onBlur={allocationFormik.handleBlur}
                value={allocationFormik.values.notes}
              />
            </div>

            {/* Submit Button */}
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => {
                  setAllocationModal(false);
                  setSelectedStock(null);
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isAllocating || !filteredProjectOptions.length}
              >
                {isAllocating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Allocating...
                  </>
                ) : (
                  "Allocate Stock"
                )}
              </button>
            </div>

            {/* Warning if no other projects available */}
            {selectedStock && filteredProjectOptions.length === 0 && (
              <div className="alert alert-warning mt-3">
                <i className="ri-alert-line me-2"></i>
                No other projects available for allocation. You can only
                allocate to different projects.
              </div>
            )}
          </Form>
        </ModalBody>
      </Modal>

      {/* Create Stock Modal */}
      <Modal
        isOpen={createModal}
        toggle={() => {
          setCreateModal(!createModal);
          setStockType("manual");
        }}
        centered
        size="lg"
      >
        <ModalHeader
          toggle={() => {
            setCreateModal(!createModal);
            setStockType("manual");
          }}
        >
          Create New Stock Entry
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={createStockFormik.handleSubmit}>
            {/* Stock Type */}
            <div className="mb-3">
              <Label for="stock_type">Stock Type *</Label>
              <Input
                type="select"
                id="stock_type"
                name="stock_type"
                onChange={handleStockTypeChange}
                onBlur={createStockFormik.handleBlur}
                value={createStockFormik.values.stock_type}
                invalid={
                  createStockFormik.touched.stock_type &&
                  !!createStockFormik.errors.stock_type
                }
              >
                <option value="manual">Manual Stock Entry</option>
                <option value="balance_from_pr">
                  Balance from Purchase Request
                </option>
                <option value="transfer">Transfer</option>
                <option value="adjustment">Adjustment</option>
              </Input>
              {createStockFormik.touched.stock_type &&
                createStockFormik.errors.stock_type && (
                  <FormFeedback type="invalid">
                    {createStockFormik.errors.stock_type as string}
                  </FormFeedback>
                )}

              {/* Type descriptions */}
              <div className="text-muted mt-1">
                {stockType === "manual" && (
                  <small>Manual entry for new stock items</small>
                )}
                {stockType === "balance_from_pr" && (
                  <small>Stock from existing purchase request balances</small>
                )}
                {stockType === "transfer" && (
                  <small>Transfer stock between projects or locations</small>
                )}
                {stockType === "adjustment" && (
                  <small>Adjust existing stock quantities (add/remove)</small>
                )}
              </div>
            </div>

            {/* Source Project (required for Balance from PR; optional for manual/transfer/adjustment per API) */}
            <div className="mb-3">
              <Label for="source_project">
                Source Project{" "}
                {stockType === "balance_from_pr" ? "*" : "(optional)"}
              </Label>
              <Input
                type="select"
                id="source_project"
                name="source_project"
                onChange={createStockFormik.handleChange}
                onBlur={createStockFormik.handleBlur}
                value={createStockFormik.values.source_project}
                invalid={
                  createStockFormik.touched.source_project &&
                  !!createStockFormik.errors.source_project
                }
              >
                <option value="">Select Project</option>
                {projectData?.map((project: any) => (
                  <option key={project.id} value={project.id}>
                    {project.name ||
                      project.project_name ||
                      `Project ${project.id}`}
                  </option>
                ))}
              </Input>
              {createStockFormik.touched.source_project &&
                createStockFormik.errors.source_project && (
                  <FormFeedback type="invalid">
                    {createStockFormik.errors.source_project as string}
                  </FormFeedback>
                )}
            </div>

            {/* Conditional fields based on stock type */}
            {stockType === "balance_from_pr" ? (
              <div className="mb-3">
                <Label for="source_purchase_request">Purchase Request *</Label>
                <Input
                  type="number"
                  id="source_purchase_request"
                  name="source_purchase_request"
                  placeholder="Enter purchase request ID"
                  onChange={createStockFormik.handleChange}
                  onBlur={createStockFormik.handleBlur}
                  value={createStockFormik.values.source_purchase_request}
                  invalid={
                    createStockFormik.touched.source_purchase_request &&
                    !!createStockFormik.errors.source_purchase_request
                  }
                />
                {createStockFormik.touched.source_purchase_request &&
                  createStockFormik.errors.source_purchase_request && (
                    <FormFeedback type="invalid">
                      {
                        createStockFormik.errors
                          .source_purchase_request as string
                      }
                    </FormFeedback>
                  )}
                <small className="text-muted">
                  Enter the purchase request ID from which to take balance
                </small>
              </div>
            ) : (
              <>
                {/* Product Name for manual/transfer/adjustment */}
                <div className="mb-3">
                  <Label for="manual_product_name">Product Name *</Label>
                  <Input
                    type="text"
                    id="manual_product_name"
                    name="manual_product_name"
                    placeholder="Enter product name"
                    onChange={createStockFormik.handleChange}
                    onBlur={createStockFormik.handleBlur}
                    value={createStockFormik.values.manual_product_name}
                    invalid={
                      createStockFormik.touched.manual_product_name &&
                      !!createStockFormik.errors.manual_product_name
                    }
                  />
                  {createStockFormik.touched.manual_product_name &&
                    createStockFormik.errors.manual_product_name && (
                      <FormFeedback type="invalid">
                        {createStockFormik.errors.manual_product_name as string}
                      </FormFeedback>
                    )}
                </div>

                {/* Model Number (optional) */}
                <div className="mb-3">
                  <Label for="model_number">Model Number (Optional)</Label>
                  <Input
                    type="text"
                    id="model_number"
                    name="model_number"
                    placeholder="Enter model number"
                    onChange={createStockFormik.handleChange}
                    onBlur={createStockFormik.handleBlur}
                    value={createStockFormik.values.model_number}
                  />
                  <small className="text-muted">
                    If provided, will try to match with existing inventory
                    product
                  </small>
                </div>
              </>
            )}

            {/* Quantity Fields */}
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label for="actual_quantity">Actual Quantity *</Label>
                  <Input
                    type="number"
                    id="actual_quantity"
                    name="actual_quantity"
                    placeholder="Enter quantity"
                    step="0.01"
                    min="0.01"
                    onChange={createStockFormik.handleChange}
                    onBlur={createStockFormik.handleBlur}
                    value={createStockFormik.values.actual_quantity}
                    invalid={
                      createStockFormik.touched.actual_quantity &&
                      !!createStockFormik.errors.actual_quantity
                    }
                  />
                  {createStockFormik.touched.actual_quantity &&
                    createStockFormik.errors.actual_quantity && (
                      <FormFeedback type="invalid">
                        {createStockFormik.errors.actual_quantity as string}
                      </FormFeedback>
                    )}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label for="balance_quantity">Balance Quantity *</Label>
                  <Input
                    type="number"
                    id="balance_quantity"
                    name="balance_quantity"
                    placeholder="Enter balance quantity"
                    step="0.01"
                    min="0"
                    onChange={createStockFormik.handleChange}
                    onBlur={createStockFormik.handleBlur}
                    value={createStockFormik.values.balance_quantity}
                    invalid={
                      createStockFormik.touched.balance_quantity &&
                      !!createStockFormik.errors.balance_quantity
                    }
                  />
                  {createStockFormik.touched.balance_quantity &&
                    createStockFormik.errors.balance_quantity && (
                      <FormFeedback type="invalid">
                        {createStockFormik.errors.balance_quantity as string}
                      </FormFeedback>
                    )}
                  <small className="text-muted">
                    Automatically set to actual quantity
                  </small>
                </div>
              </Col>
            </Row>

            {/* Price Fields - only for manual/transfer/adjustment */}
            {["manual", "transfer", "adjustment"].includes(stockType) && (
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label for="unit_price">Unit Price *</Label>
                    <Input
                      type="number"
                      id="unit_price"
                      name="unit_price"
                      placeholder="Enter unit price"
                      step="0.01"
                      min="0"
                      onChange={createStockFormik.handleChange}
                      onBlur={createStockFormik.handleBlur}
                      value={createStockFormik.values.unit_price}
                    />
                    <small className="text-muted">
                      Will calculate total cost automatically
                    </small>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label for="total_cost">Total Cost *</Label>
                    <Input
                      type="number"
                      id="total_cost"
                      name="total_cost"
                      placeholder="Enter total cost"
                      step="0.01"
                      min="0"
                      onChange={createStockFormik.handleChange}
                      onBlur={createStockFormik.handleBlur}
                      value={createStockFormik.values.total_cost}
                    />
                    <small className="text-muted">
                      Will calculate unit price automatically
                    </small>
                  </div>
                </Col>
              </Row>
            )}

            {/* Currency and Status */}
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label for="currency">Currency *</Label>
                  <Input
                    type="select"
                    id="currency"
                    name="currency"
                    onChange={createStockFormik.handleChange}
                    onBlur={createStockFormik.handleBlur}
                    value={createStockFormik.values.currency}
                    invalid={
                      createStockFormik.touched.currency &&
                      !!createStockFormik.errors.currency
                    }
                  >
                    <option value="SGD">SGD</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="MYR">MYR</option>
                  </Input>
                  {createStockFormik.touched.currency &&
                    createStockFormik.errors.currency && (
                      <FormFeedback type="invalid">
                        {createStockFormik.errors.currency as string}
                      </FormFeedback>
                    )}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label for="create_stock_status">Status *</Label>
                  <Input
                    type="select"
                    id="create_stock_status"
                    name="status"
                    onChange={createStockFormik.handleChange}
                    onBlur={createStockFormik.handleBlur}
                    value={createStockFormik.values.status}
                    invalid={
                      createStockFormik.touched.status &&
                      !!createStockFormik.errors.status
                    }
                  >
                    <option value="available">Available</option>
                    <option value="partially_allocated">
                      Partially Allocated
                    </option>
                    <option value="fully_allocated">Fully Allocated</option>
                    <option value="consumed">Consumed</option>
                    <option value="reserved">Reserved</option>
                  </Input>
                  {createStockFormik.touched.status &&
                    createStockFormik.errors.status && (
                      <FormFeedback type="invalid">
                        {createStockFormik.errors.status as string}
                      </FormFeedback>
                    )}
                </div>
              </Col>
            </Row>

            {/* Notes */}
            <div className="mb-3">
              <Label for="notes">Notes</Label>
              <Input
                type="textarea"
                id="notes"
                name="notes"
                rows="3"
                placeholder="Add any notes about this stock entry"
                onChange={createStockFormik.handleChange}
                onBlur={createStockFormik.handleBlur}
                value={createStockFormik.values.notes}
              />
            </div>

            {/* Attachments (multi-upload) */}
            <div className="mb-3">
              <Label for="stock_attachments">Attachments (optional)</Label>
              <Input
                type="file"
                id="stock_attachments"
                multiple
                onChange={(e) =>
                  setCreateStockFiles(Array.from(e.target.files || []))
                }
              />
              <small className="text-muted">
                You can upload multiple files.
              </small>
              {createStockFiles.length > 0 && (
                <ul className="mt-2 mb-0 ps-3 small">
                  {createStockFiles.map((f, idx) => (
                    <li
                      key={idx}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span className="text-truncate">{f.name}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-danger p-0 ms-2"
                        onClick={() =>
                          setCreateStockFiles((prev) =>
                            prev.filter((_, i) => i !== idx)
                          )
                        }
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Auto-calculation info for manual types */}
            {["manual", "transfer", "adjustment"].includes(stockType) && (
              <div className="alert alert-info">
                <i className="ri-information-line me-2"></i>
                <strong>Auto-calculation:</strong> If you provide unit price and
                quantity, total cost will be calculated automatically. If you
                provide total cost and quantity, unit price will be calculated
                automatically.
              </div>
            )}

            {/* Submit Button */}
            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-light"
                onClick={() => {
                  setCreateModal(false);
                  setStockType("manual");
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isCreatingStock}
              >
                {isCreatingStock ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Creating...
                  </>
                ) : (
                  "Create Stock"
                )}
              </button>
            </div>
          </Form>
        </ModalBody>
      </Modal>

      {/* Edit Stock Modal (manual / transfer / adjustment) */}
      <Modal
        isOpen={editModal}
        toggle={() => {
          setEditModal(false);
          setStockToEdit(null);
        }}
        centered
        size="lg"
      >
        <ModalHeader
          toggle={() => {
            setEditModal(false);
            setStockToEdit(null);
          }}
        >
          Edit Stock Entry
        </ModalHeader>
        <ModalBody>
          {stockToEdit && (
            <Form onSubmit={editStockFormik.handleSubmit}>
              {/* Stock type read-only */}
              <div className="mb-3">
                <Label>Stock Type</Label>
                <div>
                  <Badge color="info">
                    {getStockTypeDisplay(stockToEdit.stock_type)}
                  </Badge>
                </div>
              </div>

              {/* Source Project */}
              <div className="mb-3">
                <Label for="edit_source_project">Source Project (optional)</Label>
                <Input
                  type="select"
                  id="edit_source_project"
                  name="source_project"
                  onChange={editStockFormik.handleChange}
                  onBlur={editStockFormik.handleBlur}
                  value={editStockFormik.values.source_project}
                  invalid={
                    editStockFormik.touched.source_project &&
                    !!editStockFormik.errors.source_project
                  }
                >
                  <option value="">Select Project</option>
                  {projectData?.map((project: any) => (
                    <option key={project.id} value={project.id}>
                      {project.name ||
                        project.project_name ||
                        `Project ${project.id}`}
                    </option>
                  ))}
                </Input>
                {editStockFormik.touched.source_project &&
                  editStockFormik.errors.source_project && (
                    <FormFeedback type="invalid">
                      {editStockFormik.errors.source_project as string}
                    </FormFeedback>
                  )}
              </div>

              {/* Product Name */}
              <div className="mb-3">
                <Label for="edit_manual_product_name">Product Name *</Label>
                <Input
                  type="text"
                  id="edit_manual_product_name"
                  name="manual_product_name"
                  placeholder="Enter product name"
                  onChange={editStockFormik.handleChange}
                  onBlur={editStockFormik.handleBlur}
                  value={editStockFormik.values.manual_product_name}
                  invalid={
                    editStockFormik.touched.manual_product_name &&
                    !!editStockFormik.errors.manual_product_name
                  }
                />
                {editStockFormik.touched.manual_product_name &&
                  editStockFormik.errors.manual_product_name && (
                    <FormFeedback type="invalid">
                      {editStockFormik.errors.manual_product_name as string}
                    </FormFeedback>
                  )}
              </div>

              {/* Model Number */}
              <div className="mb-3">
                <Label for="edit_model_number">Model Number (Optional)</Label>
                <Input
                  type="text"
                  id="edit_model_number"
                  name="model_number"
                  placeholder="Enter model number"
                  onChange={editStockFormik.handleChange}
                  onBlur={editStockFormik.handleBlur}
                  value={editStockFormik.values.model_number}
                />
              </div>

              {/* Quantity */}
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label for="edit_actual_quantity">Actual Quantity *</Label>
                    <Input
                      type="number"
                      id="edit_actual_quantity"
                      name="actual_quantity"
                      placeholder="Enter quantity"
                      step="0.01"
                      min="0.01"
                      onChange={editStockFormik.handleChange}
                      onBlur={editStockFormik.handleBlur}
                      value={editStockFormik.values.actual_quantity}
                      invalid={
                        editStockFormik.touched.actual_quantity &&
                        !!editStockFormik.errors.actual_quantity
                      }
                    />
                    {editStockFormik.touched.actual_quantity &&
                      editStockFormik.errors.actual_quantity && (
                        <FormFeedback type="invalid">
                          {editStockFormik.errors.actual_quantity as string}
                        </FormFeedback>
                      )}
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label for="edit_balance_quantity">Balance Quantity *</Label>
                    <Input
                      type="number"
                      id="edit_balance_quantity"
                      name="balance_quantity"
                      placeholder="Balance quantity"
                      step="0.01"
                      min="0"
                      onChange={editStockFormik.handleChange}
                      onBlur={editStockFormik.handleBlur}
                      value={editStockFormik.values.balance_quantity}
                      invalid={
                        editStockFormik.touched.balance_quantity &&
                        !!editStockFormik.errors.balance_quantity
                      }
                    />
                  </div>
                </Col>
              </Row>

              {/* Unit Price & Total Cost */}
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label for="edit_unit_price">Unit Price</Label>
                    <Input
                      type="number"
                      id="edit_unit_price"
                      name="unit_price"
                      placeholder="Unit price"
                      step="0.01"
                      min="0"
                      onChange={editStockFormik.handleChange}
                      onBlur={editStockFormik.handleBlur}
                      value={editStockFormik.values.unit_price}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label for="edit_total_cost">Total Cost</Label>
                    <Input
                      type="number"
                      id="edit_total_cost"
                      name="total_cost"
                      placeholder="Total cost"
                      step="0.01"
                      min="0"
                      onChange={editStockFormik.handleChange}
                      onBlur={editStockFormik.handleBlur}
                      value={editStockFormik.values.total_cost}
                    />
                  </div>
                </Col>
              </Row>

              {/* Currency & Status */}
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <Label for="edit_currency">Currency *</Label>
                    <Input
                      type="select"
                      id="edit_currency"
                      name="currency"
                      onChange={editStockFormik.handleChange}
                      onBlur={editStockFormik.handleBlur}
                      value={editStockFormik.values.currency}
                      invalid={
                        editStockFormik.touched.currency &&
                        !!editStockFormik.errors.currency
                      }
                    >
                      <option value="SGD">SGD</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="MYR">MYR</option>
                    </Input>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <Label for="edit_status">Status *</Label>
                    <Input
                      type="select"
                      id="edit_status"
                      name="status"
                      onChange={editStockFormik.handleChange}
                      onBlur={editStockFormik.handleBlur}
                      value={editStockFormik.values.status}
                      invalid={
                        editStockFormik.touched.status &&
                        !!editStockFormik.errors.status
                      }
                    >
                      <option value="available">Available</option>
                      <option value="partially_allocated">
                        Partially Allocated
                      </option>
                      <option value="fully_allocated">Fully Allocated</option>
                      <option value="consumed">Consumed</option>
                      <option value="reserved">Reserved</option>
                    </Input>
                  </div>
                </Col>
              </Row>

              {/* Notes */}
              <div className="mb-3">
                <Label for="edit_notes">Notes</Label>
                <Input
                  type="textarea"
                  id="edit_notes"
                  name="notes"
                  rows="3"
                  placeholder="Notes"
                  onChange={editStockFormik.handleChange}
                  onBlur={editStockFormik.handleBlur}
                  value={editStockFormik.values.notes}
                />
              </div>

              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    setEditModal(false);
                    setStockToEdit(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isUpdatingStock}
                >
                  {isUpdatingStock ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Updating...
                    </>
                  ) : (
                    "Update Stock"
                  )}
                </button>
              </div>
            </Form>
          )}
        </ModalBody>
      </Modal>

      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Stock Management" pageTitle="Inventory" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">Stock Management List</h5>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-primary"
                        onClick={() => setCreateModal(true)}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Create Stock
                      </button>
                      {IsValidPermissions(["ADD_STOCK_MANAGEMENT"]) && (
                        <Link
                          to="/stock-management/create"
                          className="btn btn-outline-primary"
                        >
                          <i className="ri-file-list-line align-bottom me-1"></i>
                          Create from PR
                        </Link>
                      )}
                    </div>
                  </div>
                </CardHeader>

                <div className="card-body pt-0">
                  {isLoading ? (
                    <Loader />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={stockData?.results || []}
                      isLoading={isFetching}
                      isGlobalFilter={true}
                      SearchPlaceholder="Search stock..."
                      isPagination={false}
                      isServerPagination={true}
                      customPageSize={page.pageSize}
                      serverPaginationProps={{
                        count: stockData?.count || 0,
                        next: stockData?.next || null,
                        previous: stockData?.previous || null,
                        current_page: stockData?.current_page || 1,
                        total_page: stockData?.total_page || 0,
                        page_size: stockData?.page_size || 10,
                      }}
                      setPageState={setPageState}
                      divClass="table-responsive table-card mb-3"
                      tableClass="align-middle table-nowrap mb-0"
                      theadClass="table-light text-muted"
                    />
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default StockManagementListView;
